import React, { useState, useEffect, useRef } from 'react';
import { useDrop } from 'react-dnd';
import Card, { MiniCard, ItemTypes } from './Card';

function DrawDiscardPiles({
  deck,
  canDraw = false,
  canDiscard = false,
  onDrawFromDeck,
  onDrawFromDiscard,
  onDiscard
}) {
  const drawPileCount = deck?.drawPileCount || 0;
  const discardPileCount = deck?.discardPileCount || 0;
  const topDiscard = deck?.topDiscard;
  const discardHistory = deck?.discardHistory || [];

  // State for showing discard history popup
  const [showHistory, setShowHistory] = useState(false);

  // Track deck count changes for animation
  const [deckAnimation, setDeckAnimation] = useState(null);
  const prevCountRef = useRef(drawPileCount);

  // Track discard pile changes for animation
  const [discardAnimation, setDiscardAnimation] = useState(null);
  const prevDiscardRef = useRef(topDiscard?.id);

  useEffect(() => {
    if (prevCountRef.current !== drawPileCount && prevCountRef.current !== 0) {
      const diff = drawPileCount - prevCountRef.current;
      if (diff !== 0) {
        setDeckAnimation(diff < 0 ? 'draw' : 'add');
        const timer = setTimeout(() => setDeckAnimation(null), 500);
        return () => clearTimeout(timer);
      }
    }
    prevCountRef.current = drawPileCount;
  }, [drawPileCount]);

  // Animate discard pile when top card changes (card picked up or new card added)
  useEffect(() => {
    const currentId = topDiscard?.id;
    if (prevDiscardRef.current && currentId !== prevDiscardRef.current) {
      // Card changed - animate based on whether a card was added or removed
      // If new top card exists and is different, a card was likely picked up (showing previous card)
      setDiscardAnimation('change');
      const timer = setTimeout(() => setDiscardAnimation(null), 400);
      prevDiscardRef.current = currentId;
      return () => clearTimeout(timer);
    }
    prevDiscardRef.current = currentId;
  }, [topDiscard?.id]);

  // Drop zone for discarding cards
  const [{ isOver, canDrop }, drop] = useDrop(() => ({
    accept: ItemTypes.CARD,
    canDrop: () => canDiscard,
    drop: (item) => {
      if (onDiscard) {
        onDiscard(item.card);
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop()
    })
  }), [canDiscard, onDiscard]);

  return (
    <div className="flex items-end gap-4 sm:gap-8">
      {/* Draw pile */}
      <div className="relative">
        <button
          onClick={onDrawFromDeck}
          disabled={!canDraw}
          className={`
            relative
            transition-transform duration-200
            ${canDraw ? 'hover:scale-105 active:scale-95 cursor-pointer' : 'cursor-not-allowed opacity-75'}
          `}
        >
          {/* Stack effect - offset to bottom-left instead of bottom-right so it doesn't push the bottom down further than the card itself (since items-end aligns the bottom of the *container*, we want the container bottom to be the *card* bottom, not the shadow bottom) 
              Actually, the simplest way is to put the shadow *behind* and offset to the top-right. */}
          <div className="absolute -top-1 -right-1 w-12 h-16 sm:w-16 sm:h-24 bg-indigo-900 rounded-lg" />
          <div className="absolute -top-0.5 -right-0.5 w-12 h-16 sm:w-16 sm:h-24 bg-indigo-800 rounded-lg" />

          {/* Top card (face down) */}
          <Card
            card={{ type: 'number', value: 1, color: 'blue' }}
            faceDown
          />

          {/* Card count badge - animated on change */}
          <div
            className="absolute -bottom-1 -right-1 sm:-bottom-2 sm:-right-2
            text-white px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full
            transition-all duration-300"
            style={{
              fontFamily: '"Cinzel", Georgia, serif',
              fontSize: '0.6rem', fontWeight: '700',
              background: deckAnimation === 'draw' ? '#e67e22' : deckAnimation === 'add' ? '#27ae60' : 'rgba(13,31,20,0.9)',
              border: '1px solid rgba(212,168,67,0.4)',
              transform: deckAnimation ? 'scale(1.2)' : 'scale(1)',
            }}
          >
            {drawPileCount}
          </div>
        </button>

        <div style={{
          textAlign: 'center', marginTop: '6px',
          fontFamily: '"IM Fell English SC", Georgia, serif',
          fontSize: '0.68rem', letterSpacing: '0.08em',
          color: 'rgba(245,234,210,0.5)',
        }}>
          Draw
        </div>
      </div>

      {/* Discard pile */}
      <div className="relative">
        <div
          ref={drop}
          className={`
            relative transition-all duration-200
            ${isOver && canDrop ? 'ring-2 ring-accent-gold shadow-[0_0_15px_rgba(212,168,67,0.6)] rounded-lg' : ''}
            ${canDrop && !isOver ? 'ring-2 ring-dashed ring-accent-gold/50 rounded-lg' : ''}
          `}
        >
          {topDiscard ? (
            <button
              onClick={onDrawFromDiscard}
              disabled={!canDraw || topDiscard.type === 'skip'}
              className={`
                relative transition-transform duration-200
                ${canDraw && topDiscard.type !== 'skip' ? 'hover:scale-105 active:scale-95 cursor-pointer' : ''}
                ${!canDraw && !canDiscard ? 'cursor-not-allowed opacity-75' : ''}
                ${discardAnimation === 'change' ? 'animate-discard-change' : ''}
              `}
            >
              <Card card={topDiscard} draggable={false} />
              {topDiscard.type === 'skip' && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-lg backdrop-blur-[1px]">
                  <div className="flex flex-col items-center justify-center text-white bg-red-600/90 px-1.5 sm:px-2 py-1 rounded-md font-bold text-[9px] sm:text-[11px] leading-tight shadow-lg border border-red-400/50 max-w-[85%] text-center">
                    <span className="text-[12px] sm:text-[14px] mb-0.5 leading-none">🚫</span>
                    <span>NO<br/>TAKE</span>
                  </div>
                </div>
              )}
              {/* Count badge — inside button, follows the card */}
              {discardPileCount > 0 && (
                <div
                  className="absolute -bottom-1 -right-1 sm:-bottom-2 sm:-right-2
                    bg-gray-700 text-white text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full
                    font-bold cursor-pointer hover:bg-gray-600 transition-colors"
                  onClick={(e) => { e.stopPropagation(); setShowHistory(!showHistory); }}
                  title="Click to see discard history"
                >
                  {discardPileCount}
                </div>
              )}
            </button>
          ) : (
            <div className={`
              w-12 h-16 sm:w-16 sm:h-24 border-2 border-dashed rounded-lg flex flex-col items-center justify-center text-xs sm:text-sm transition-all
              ${isOver && canDrop ? 'border-accent-gold text-accent-gold bg-accent-gold/20 shadow-[0_0_15px_rgba(250,204,21,0.5)]' : 'border-white/20 text-gray-500 bg-black/20 hover:bg-black/40'}
            `}>
              <span className="text-xl mb-1 opacity-50">📥</span>
              <span className="font-medium">{isOver && canDrop ? 'Drop!' : 'Empty'}</span>
            </div>
          )}

          {/* Drop indicator overlay */}
          {isOver && canDrop && topDiscard && (
            <div className="absolute inset-0 flex items-center justify-center bg-accent-gold/30 rounded-lg pointer-events-none">
              <span style={{
                fontFamily: '"Cinzel", Georgia, serif',
                fontSize: '0.65rem', fontWeight: '700', letterSpacing: '0.1em',
                color: '#0d1f14',
                background: '#d4a843', padding: '2px 10px', borderRadius: '4px',
              }}>
                DISCARD
              </span>
            </div>
          )}

          {/* Discard history popup */}
          {showHistory && discardHistory.length > 0 && (
              <div
                className="absolute top-full mt-2 left-1/2 -translate-x-1/2 z-50
                  border rounded-lg p-3 shadow-xl min-w-[150px]"
                style={{
                  background: 'rgba(13,31,20,0.97)',
                  border: '1px solid rgba(212,168,67,0.3)',
                  backdropFilter: 'blur(8px)',
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div style={{
                  fontFamily: '"Cinzel", Georgia, serif',
                  fontSize: '0.62rem', letterSpacing: '0.12em',
                  color: 'rgba(212,168,67,0.7)', textAlign: 'center', marginBottom: '8px',
                }}>Recent Discards</div>
                <div className="flex flex-wrap gap-1 justify-center">
                  {discardHistory.map((card, idx) => (
                    <MiniCard key={card.id || idx} card={card} />
                  ))}
                </div>
                <button
                  style={{
                    marginTop: '8px',
                    fontFamily: '"IM Fell English SC", Georgia, serif',
                    fontSize: '0.65rem', letterSpacing: '0.06em',
                    color: 'rgba(245,234,210,0.4)', width: '100%', textAlign: 'center',
                    background: 'none', border: 'none', cursor: 'pointer',
                    transition: 'color 0.15s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.color = '#f5ead2'}
                  onMouseLeave={e => e.currentTarget.style.color = 'rgba(245,234,210,0.4)'}
                  onClick={() => setShowHistory(false)}
                >
                  Close
                </button>
              </div>
          )}
        </div>

        <div style={{
          textAlign: 'center', marginTop: '6px',
          fontFamily: '"IM Fell English SC", Georgia, serif',
          fontSize: '0.68rem', letterSpacing: '0.08em',
          color: 'rgba(245,234,210,0.5)',
        }}>
          Discard
          {topDiscard?.type === 'skip' && (
            <span style={{
              display: 'block',
              fontFamily: '"IM Fell English SC", Georgia, serif',
              fontSize: '0.6rem', color: 'rgba(231,76,60,0.85)',
              letterSpacing: '0.04em',
            }}>Cannot take Skip</span>
          )}
        </div>
      </div>
    </div>
  );
}

// Compact version for smaller displays
export function CompactPiles({ deck, canDraw, onDrawFromDeck, onDrawFromDiscard }) {
  const drawPileCount = deck?.drawPileCount || 0;
  const topDiscard = deck?.topDiscard;

  return (
    <div className="flex items-center gap-4">
      <button
        onClick={onDrawFromDeck}
        disabled={!canDraw}
        className={`
          flex items-center gap-2 px-4 py-2 rounded-lg
          ${canDraw ? 'bg-indigo-600 hover:bg-indigo-500' : 'bg-gray-600 opacity-50'}
          transition-colors
        `}
      >
        <span className="text-2xl">🃏</span>
        <span>Draw ({drawPileCount})</span>
      </button>

      <button
        onClick={onDrawFromDiscard}
        disabled={!canDraw || !topDiscard}
        className={`
          flex items-center gap-2 px-4 py-2 rounded-lg
          ${canDraw && topDiscard ? 'bg-accent-gold/20 hover:bg-accent-gold/30 border border-accent-gold' : 'bg-gray-600 opacity-50'}
          transition-colors
        `}
      >
        {topDiscard ? (
          <>
            <Card card={topDiscard} small />
            <span>Take</span>
          </>
        ) : (
          <span className="text-gray-400">Discard Empty</span>
        )}
      </button>
    </div>
  );
}

export default DrawDiscardPiles;
